import { effectivePermissionSetHas } from '@utazzo/shared';
import { UzAvatar, UzDropdownMenu, UzPopover, UzTooltip, useBranding } from '@utazzo/ui';
import {
  ChevronDown,
  ChevronRight,
  HelpCircle,
  LogOut,
  Menu as MenuIcon,
  PanelLeftClose,
  PanelLeftOpen,
  User as UserIcon,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { IS_SERVICES, canSeeNavItem, isServicesAllowedRole } from './app-variant';
import { CommandPalette } from './CommandPalette';
import {
  homePathForRole,
  isMarketingDashboardRole,
  MARKETING_DASHBOARD_PATH,
  WELCOME_PATH,
} from './home-path';
import { NAV_GROUPS, NAV_ITEMS, type NavGroupConfig, type NavItemConfig } from './nav-config';
import { onSidebarCollapseRequest } from './sidebar-collapse';
import { useWelcomeGate } from './welcome-gate';

import { IdleLogout } from '@/features/auth/idle/IdleLogout';
import { useCurrentUser, useLogout } from '@/features/auth/use-auth';
import { useCrossTabAuthSync } from '@/features/auth/use-cross-tab-auth-sync';
import { useCatalogApprovalsPendingCount } from '@/features/catalog-approvals/use-catalog-approvals';
import { useSupplierChangeApprovalsPendingCount } from '@/features/invoices/use-supplier-change-approvals';
import { useRepriceApprovalsPendingCount } from '@/features/itineraries/use-reprice-approvals';
import { useVoidApprovalsPendingCount } from '@/features/itineraries/use-void-approvals';
import {
  useCanSeeLeadApprovals,
  useLeadApprovalsPendingCount,
} from '@/features/leads/use-lead-approvals';
import { useExitUnassignedCount } from '@/features/leads/use-leads';
import {
  useMyEffectivePermissions,
  useSyncSessionUser,
} from '@/features/my-profile/use-my-profile';
import { useNavMru } from '@/features/nav-mru/use-nav-mru';
import { InboxBell } from '@/features/notifications/components/InboxBell';
import { NotificationPopupHost } from '@/features/notifications/components/NotificationPopupHost';
import { MobileInstallBanner } from '@/features/push/components/MobileInstallBanner';
import { PushPermissionBanner } from '@/features/push/components/PushPermissionBanner';
import { PushLaunchBridge } from '@/features/push/PushLaunchBridge';
import { AppUpdateChip } from '@/features/pwa/AppUpdateChip';
import { AppUpdateWatcher } from '@/features/pwa/AppUpdateWatcher';
import { PullToRefresh } from '@/features/pwa/PullToRefresh';
import { useTravelBookingsSummary } from '@/features/travel-bookings/use-travel-bookings';
import { useWebsiteApprovalsPendingCount } from '@/features/website-approvals/use-website-approvals';
import { usePermissions } from '@/features/website-cms/lib';
import { WhatsAppWidget } from '@/features/whatsapp/WhatsAppWidget';
import { cn } from '@/lib/cn';
import { useCommandPaletteStore } from '@/stores/command-palette-store';

const COLLAPSE_KEY = 'utazzo.admin.sidebar.collapsed';
const GROUP_COLLAPSE_KEY = 'utazzo.admin.sidebar.groups';

// Accounts for which the sidebar brand (wordmark logo + square icon mark) is
// hidden. Requested per-user (e.g. white-label / screenshot use); matched by
// case-insensitive exact email. Purely cosmetic - no data or route is gated.
const BRAND_HIDDEN_EMAILS = new Set(['productteam@utazzoholidays.com']);

export const AppShell = (): React.JSX.Element => {
  const { t } = useTranslation();
  const branding = useBranding();
  const user = useCurrentUser();
  // The page scroll area - pull-to-refresh listens here (installed app, phones).
  const mainRef = useRef<HTMLElement>(null);
  const hideBranding = user?.email
    ? BRAND_HIDDEN_EMAILS.has(user.email.trim().toLowerCase())
    : false;
  const logout = useLogout();
  const { role, can } = usePermissions();
  const effectivePermissions = useMyEffectivePermissions();
  // Keeps the persisted session role in step with /me, so an admin's role
  // change lands without the user having to sign out (see useSyncSessionUser).
  useSyncSessionUser();
  // Log out (or in) every open tab together - a sign-out in one tab flips the
  // rest to /login instead of leaving them on a stale cached session.
  useCrossTabAuthSync();
  const location = useLocation();

  // Welcome gate: until a non-admin has scrolled the whole Welcome Utazzer page
  // this session, bounce every other route back to /welcome so nobody skips it
  // (including a re-login that deep-linked back to the page they were on). ADMIN
  // is exempt from the mandatory scroll - admins land on Welcome but can navigate
  // away immediately. The single-screen marketing role is exempt too (below).
  const welcomeAcknowledged = useWelcomeGate((s) => s.acknowledged);
  // The marketing-only role (marketing_in) skips the Welcome page entirely: no
  // force, no scroll gate, and it lands straight on the Marketing dashboard.
  // Keyed on the role code (role-specific - works for every user holding it).
  const isMarketingDashboardOnly = isMarketingDashboardRole(user?.assignedRoleCodes);
  const mustSeeWelcome =
    !!user &&
    user.role !== 'ADMIN' &&
    !isMarketingDashboardOnly &&
    !welcomeAcknowledged &&
    location.pathname !== WELCOME_PATH;
  // Never show these users the Welcome page - bounce it (and the login landing)
  // to the Marketing dashboard.
  const sendMarketingHome = isMarketingDashboardOnly && location.pathname === WELCOME_PATH;

  const [mobileOpen, setMobileOpen] = useState(false);
  const cmdOpen = useCommandPaletteStore((s) => s.open);
  const cmdInitialQuery = useCommandPaletteStore((s) => s.initialQuery);
  const openPalette = useCommandPaletteStore((s) => s.openPalette);
  const closePalette = useCommandPaletteStore((s) => s.closePalette);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  // Persist collapse + close mobile drawer on route change.
  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // External pages can request collapse/expand via the sidebar-collapse
  // event bus - used by `LeadDetailPage` to claim the two-pane area on
  // mount. We honor every request without trying to be clever; the
  // operator can still toggle manually after.
  useEffect(() => {
    const off = onSidebarCollapseRequest((next) => setCollapsed(next));
    return off;
  }, []);

  // Global ⌘K / Ctrl+K to open the command palette.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (useCommandPaletteStore.getState().open) closePalette();
        else openPalette();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPalette, closePalette]);

  const initials = user?.fullName
    ? user.fullName
        .split(' ')
        .map((p) => p[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : 'U';

  // The Services (accounts) app is restricted to ADMIN + FINANCE. A signed-in
  // user of any other role who lands on utazzoservices.com gets a no-access
  // notice instead of a CRM they can't use here. (The API is role-gated too;
  // this is the UI boundary.)
  if (IS_SERVICES && role != null && !isServicesAllowedRole(role)) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-[var(--z-50)] p-6 text-center">
        <h1 className="text-xl font-semibold text-[var(--fg)]">No access to Utazzo Services</h1>
        <p className="max-w-md text-sm text-[var(--fg-muted)]">
          The Services app is for the accounts team. Please use the main CRM, or sign in with an
          account that has access.
        </p>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className="rounded-[var(--r-md)] border px-4 py-2 text-sm"
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div
      // Mobile/tablet (< lg): a single full-width column - the sidebar is
      // hidden (it lives in the slide-over drawer), so the main content must
      // own the whole width. Desktop (lg+): the `sidebar | main` two-column
      // track, sized via the --shell-cols variable so the collapse animation
      // still works. The sizing is a CSS var (not a direct inline
      // grid-template-columns) so it can't override the mobile grid-cols-1.
      className="grid h-screen grid-cols-1 overflow-hidden bg-[var(--z-50)] lg:grid-cols-[var(--shell-cols)]"
      style={
        {
          '--shell-cols': `${collapsed ? '64px' : '240px'} 1fr`,
          transition: 'grid-template-columns var(--base) var(--ease)',
        } as React.CSSProperties
      }
    >
      {/* Sidebar (desktop) */}
      <Sidebar
        collapsed={collapsed}
        role={role}
        can={can}
        effectivePermissions={effectivePermissions}
        branding={branding}
        hideBranding={hideBranding}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        className="hidden lg:flex"
      />

      {/* Sidebar (mobile drawer) */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-[var(--z-950)]/40"
            onClick={() => setMobileOpen(false)}
            aria-label={t('shell.topbar.closeMenu')}
          />
          <div className="absolute inset-y-0 left-0 w-60">
            <Sidebar
              collapsed={false}
              role={role}
              can={can}
              effectivePermissions={effectivePermissions}
              branding={branding}
              hideBranding={hideBranding}
              onToggleCollapse={() => setMobileOpen(false)}
              mobile
              // Pin the drawer to the viewport height so the nav list inside
              // (flex-1 + overflow-y-auto) scrolls instead of growing past it.
              className="h-full"
            />
          </div>
        </div>
      )}

      {/* Main column - inside the WhatsApp provider so the /whatsapp inbox
          page shares the one softphone with the floating bubble. */}
      <WhatsAppWidget>
        <div className="flex min-w-0 flex-col overflow-hidden">
          <Topbar
            onOpenMobileNav={() => setMobileOpen(true)}
            user={user}
            initials={initials}
            onSignOut={() => logout.mutate()}
          />
          <PullToRefresh target={mainRef} />
          <main
            ref={mainRef}
            className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain bg-[var(--z-50)] px-4 py-6 lg:px-8 lg:py-8"
          >
            {mustSeeWelcome ? (
              <Navigate to={WELCOME_PATH} replace />
            ) : sendMarketingHome ? (
              <Navigate to={MARKETING_DASHBOARD_PATH} replace />
            ) : (
              <Outlet />
            )}
          </main>
        </div>
      </WhatsAppWidget>

      <CommandPalette
        open={cmdOpen}
        onClose={closePalette}
        role={role}
        can={can}
        initialQuery={cmdInitialQuery}
      />

      {/* Phone / desktop push: notification taps, subscription sync. After
          the widget so its bus listeners are subscribed first. */}
      <PushLaunchBridge />

      {/* Inactivity auto-logout: 10 min idle -> warning, +5 min -> logout.
          Multi-tab safe (one shared clock), client-side (no idle polling).
          Off on phones - see IdleLogout. */}
      <IdleLogout />

      {/* Post-login "install the app" nudge, phones only (hidden once the app
          is installed or the user dismisses it). Adapts to Android vs iOS. */}
      <MobileInstallBanner />

      {/* Installed app only: "turn on notifications" nudge (the install
          banner above never shows there, so the two can't stack), and a
          "new version - reload" offer after a deploy. */}
      <PushPermissionBanner />
      <AppUpdateWatcher />
    </div>
  );
};

/* ─── Sidebar ─── */

interface SidebarProps {
  collapsed: boolean;
  role: ReturnType<typeof usePermissions>['role'];
  can: ReturnType<typeof usePermissions>['can'];
  /// Effective permission set from /me (own + inherited via role chain).
  /// Drives `requirePermission` nav-item gating - see canAccess().
  effectivePermissions: Set<string> | null | undefined;
  branding: {
    appName: string;
    logoUrl: string | null;
    iconUrl: string | null;
    logoBgColor: string | null;
  };
  onToggleCollapse: () => void;
  className?: string;
  mobile?: boolean;
  hideBranding?: boolean;
}

const Sidebar = ({
  collapsed,
  role,
  can,
  effectivePermissions,
  branding,
  onToggleCollapse,
  className,
  mobile,
  hideBranding,
}: SidebarProps): React.JSX.Element => {
  const { t } = useTranslation();
  const sidebarUser = useCurrentUser();
  // 2026-07-22 (ADR-0069). Was role-gated, which left the badge dark for a
  // role an admin had explicitly granted `ops.lead_approvals.read` from the
  // /roles matrix. Now it follows the nav item itself: reviewers see their
  // queue count, requesters see how many of their own requests are pending.
  // The predicate lives in `useCanSeeLeadApprovals` so the Daily Tracker's
  // Pending Approvals tile gates identically - it reads the same
  // auth-store role + /me permission set this component's props derive from.
  const showApprovalsNav = useCanSeeLeadApprovals();
  const pendingApprovalsQuery = useLeadApprovalsPendingCount(showApprovalsNav);
  const pendingApprovalsCount = pendingApprovalsQuery.data?.count ?? 0;

  // 2026-07-23. Same badge treatment for Product approvals, which sits
  // directly under Lead approvals in this group and previously showed no
  // count at all - an admin had to open the page to learn whether anything
  // was waiting. Gated on the SAME permission the nav item declares
  // (`sys.roles.read` in nav-config), read straight off the effective set, so
  // the query can never fire for someone who cannot see the item.
  const showCatalogApprovalsNav = Boolean(
    effectivePermissions && effectivePermissionSetHas(effectivePermissions, 'sys.roles.read'),
  );
  const catalogApprovalsQuery = useCatalogApprovalsPendingCount(showCatalogApprovalsNav);
  const catalogApprovalsCount = catalogApprovalsQuery.data?.pagination.totalItems ?? 0;

  // 2026-07-29. Same badge for the exit-unassigned queue, which sits right
  // under Pending approvals: released leads are the other "someone has to
  // act on this" pile, and it was the only one you had to open the page to
  // size. Gated on the SAME permission the nav item declares
  // (`ops.exit_unassigned.read` in nav-config), read off the effective set,
  // so the query never fires for someone who cannot see the item.
  const showExitUnassignedNav = Boolean(
    effectivePermissions &&
    effectivePermissionSetHas(effectivePermissions, 'ops.exit_unassigned.read'),
  );
  const exitUnassignedQuery = useExitUnassignedCount(showExitUnassignedNav);
  const exitUnassignedCount = exitUnassignedQuery.data?.pagination.totalItems ?? 0;

  // Same badge again for the website content review queue, so all three
  // "someone has to act on this" piles size themselves in the rail. Gated on
  // the SAME permission the nav item declares (`web.destination_pages.read`),
  // so the query never fires for someone who cannot see the item.
  const showWebsiteApprovalsNav = Boolean(
    effectivePermissions &&
    effectivePermissionSetHas(effectivePermissions, 'web.destination_pages.read'),
  );
  const websiteApprovalsQuery = useWebsiteApprovalsPendingCount(showWebsiteApprovalsNav);
  const websiteApprovalsCount = websiteApprovalsQuery.data?.pagination.totalItems ?? 0;

  // Same badge for the itinerary-reprice approvals queue. Gated on the SAME
  // permission the nav item declares (`ops.itineraries.read` in
  // nav-config - it shares the itineraries module's read code, since ADMIN /
  // SALES_LEAD see it as reviewers and SALES_AGENT sees their own
  // submissions read-only), so the query never fires for someone who cannot
  // see the item.
  const showRepriceApprovalsNav = Boolean(
    effectivePermissions && effectivePermissionSetHas(effectivePermissions, 'ops.itineraries.read'),
  );
  const repriceApprovalsQuery = useRepriceApprovalsPendingCount(showRepriceApprovalsNav);
  const repriceApprovalsCount = repriceApprovalsQuery.data?.count ?? 0;

  // Void-a-won-deal approvals queue badge - same gate + scoping as reprice.
  const voidApprovalsQuery = useVoidApprovalsPendingCount(showRepriceApprovalsNav);
  const voidApprovalsCount = voidApprovalsQuery.data?.count ?? 0;

  // Supplier-change approvals badge (Services app, ADMIN only).
  const supplierChangeQuery = useSupplierChangeApprovalsPendingCount(
    IS_SERVICES && role === 'ADMIN',
  );
  const supplierChangeCount = supplierChangeQuery.data?.count ?? 0;

  // 2026-09-07. Same badge for Ticketing > Action needed: paid website
  // bookings the supplier refused for our account, waiting on an admin.
  // Nothing refunds them on a timer any more, so the count is the only thing
  // that says "guest money is waiting". ADMIN by role, like the nav item.
  const ticketingSummary = useTravelBookingsSummary(role === 'ADMIN');
  const ticketingActionCount = ticketingSummary.data?.actionNeeded ?? 0;

  // Per-group expand/collapse (only meaningful when the rail is expanded).
  // Defaults to open; persisted so a closed section stays closed across loads.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    if (typeof window === 'undefined') return {};
    try {
      return JSON.parse(window.localStorage.getItem(GROUP_COLLAPSE_KEY) || '{}') as Record<
        string,
        boolean
      >;
    } catch {
      return {};
    }
  });
  const toggleGroup = (id: string): void =>
    setOpenGroups((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? true) };
      window.localStorage.setItem(GROUP_COLLAPSE_KEY, JSON.stringify(next));
      return next;
    });

  // Per-item badge (pending counts). Shared by the expanded list and the
  // collapsed section flyouts so a count never shows in one place but not the
  // other.
  const badgeFor = (id: string): { count?: number; noun?: string } => {
    switch (id) {
      case 'lead-approvals':
        return { count: pendingApprovalsCount };
      case 'catalog-approvals':
        return { count: catalogApprovalsCount };
      case 'website-approvals':
        return { count: websiteApprovalsCount };
      case 'exit-unassigned':
        return { count: exitUnassignedCount, noun: 'unassigned' };
      case 'itinerary-reprice-approvals':
        return { count: repriceApprovalsCount };
      case 'itinerary-void-approvals':
        return { count: voidApprovalsCount };
      case 'supplier-change-approvals':
        return { count: supplierChangeCount };
      case 'ticketing-action':
        return { count: ticketingActionCount, noun: 'on hold' };
      default:
        return {};
    }
  };

  // Square brand mark on a gradient tile. The tile gives the icon a background
  // so a white / transparent logo stays visible against the light sidebar.
  // Falls back to the brand initial when no icon is configured.
  const brandMark = (
    <div
      className="inline-flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center overflow-hidden rounded-[var(--r-md)] text-white"
      style={{ background: 'linear-gradient(135deg, var(--accent-700), var(--accent-900))' }}
    >
      {branding.iconUrl ? (
        <img src={branding.iconUrl} alt={branding.appName} className="h-4 w-4 object-contain" />
      ) : (
        <span className="text-[14px] font-semibold">{branding.appName.charAt(0)}</span>
      )}
    </div>
  );

  return (
    <aside
      className={cn(
        'flex min-h-0 flex-col overflow-hidden border-r border-[var(--ds-border)] bg-[var(--bg)]',
        className,
      )}
    >
      {/* Brand. Collapsed: the square mark tile. Expanded: the full wordmark,
          or the mark tile + name when no wordmark logo is configured. */}
      <div className="flex h-[49px] flex-shrink-0 items-center gap-2 border-b border-[var(--ds-border)] px-3.5">
        {hideBranding ? null : collapsed ? (
          brandMark
        ) : branding.logoUrl ? (
          // Expanded rail: full wordmark logo (already includes the brand name).
          // When a logo background colour is configured, frame the artwork in a
          // padded tile so a white / transparent wordmark stays visible.
          <img
            src={branding.logoUrl}
            alt={branding.appName}
            className={cn(
              'mr-auto h-7 w-auto max-w-[170px] object-contain',
              branding.logoBgColor && 'rounded-(--r-md) px-2 py-1',
            )}
            style={branding.logoBgColor ? { backgroundColor: branding.logoBgColor } : undefined}
          />
        ) : (
          // No wordmark configured: mark tile + name + sublabel.
          <div className="mr-auto flex items-center gap-2">
            {brandMark}
            <div className="min-w-0 leading-tight">
              <div className="truncate text-[14px] font-semibold tracking-[-0.01em] text-[var(--fg)]">
                {branding.appName}
              </div>
              <div className="text-[11px] tracking-[0.04em] text-[var(--fg-faint)] uppercase">
                {t('shell.sidebar.brandSub')}
              </div>
            </div>
          </div>
        )}
        {mobile && (
          <button
            type="button"
            onClick={onToggleCollapse}
            className="rounded-[var(--r-md)] p-1 text-[var(--fg-faint)] hover:bg-[var(--z-100)] hover:text-[var(--fg)]"
            aria-label={t('shell.topbar.closeMenu')}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Nav. Items the caller's role / permissions don't allow are
          filtered out entirely - we don't ship a disabled placeholder
          with a lock icon. Groups that end up empty after filtering are
          hidden too, so a SALES_AGENT doesn't see a phantom "System"
          header with nothing under it. */}
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-2 py-3">
        {NAV_GROUPS.map((grp) => {
          const visibleItems = grp.items.filter(
            (item) =>
              !item.hiddenFromNav &&
              canSeeNavItem(
                item,
                role,
                can,
                effectivePermissions,
                sidebarUser?.email,
                sidebarUser?.assignedRoleCodes,
              ),
          );
          if (visibleItems.length === 0) return null;

          // Collapsed rail: one PARENT icon for the whole section. Clicking it
          // opens a flyout with the section's items at full width (icon + name),
          // so the rail stays readable instead of a wall of ungrouped icons.
          if (collapsed) {
            return (
              <CollapsedGroupFlyout
                key={grp.id}
                group={grp}
                label={t(`shell.nav.section.${grp.id}`)}
                items={visibleItems}
                labelFor={(id) => t(`shell.nav.${id}`)}
                badgeFor={badgeFor}
              />
            );
          }

          const groupOpen = openGroups[grp.id] ?? true;
          return (
            <div key={grp.id} className="flex flex-col gap-px pb-2">
              <button
                type="button"
                onClick={() => toggleGroup(grp.id)}
                aria-expanded={groupOpen}
                className="flex items-center gap-1 rounded-[var(--r-md)] px-2 pt-1.5 pb-1 text-[10.5px] font-medium tracking-[0.07em] text-[var(--fg-faint)] uppercase transition-colors hover:text-[var(--fg-muted)]"
              >
                <ChevronDown
                  className={cn('h-3 w-3 transition-transform', !groupOpen && '-rotate-90')}
                />
                <span>{t(`shell.nav.section.${grp.id}`)}</span>
              </button>
              {groupOpen &&
                visibleItems.map((item) => {
                  const badge = badgeFor(item.id);
                  return (
                    <SidebarItem
                      key={item.id}
                      item={item}
                      label={t(`shell.nav.${item.id}`)}
                      collapsed={false}
                      badgeCount={badge.count}
                      badgeNoun={badge.noun}
                    />
                  );
                })}
            </div>
          );
        })}
      </nav>

      {/* Collapse toggle (desktop only) */}
      {!mobile && (
        <div className="flex-shrink-0 border-t border-[var(--ds-border)] p-2">
          <button
            type="button"
            onClick={onToggleCollapse}
            className={cn(
              'flex w-full items-center gap-2.5 rounded-[var(--r-md)] px-2 py-1.5 text-[14px] text-[var(--fg-muted)] transition-colors hover:bg-[var(--z-100)] hover:text-[var(--fg)]',
              collapsed && 'justify-center',
            )}
            aria-label={collapsed ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4 flex-shrink-0" />
            ) : (
              <>
                <PanelLeftClose className="h-4 w-4 flex-shrink-0" />
                <span>{t('shell.sidebar.collapse')}</span>
              </>
            )}
          </button>
        </div>
      )}
    </aside>
  );
};

interface SidebarItemProps {
  item: NavItemConfig;
  label: string;
  collapsed: boolean;
  badgeCount?: number;
  /** Screen-reader noun for the badge - "12 pending", "12 unassigned". */
  badgeNoun?: string;
}

// Restricted items are filtered out one level up - this component only
// renders allowed items. The previous "disabled + Lock icon" UX leaked the
// existence of pages the operator can't reach, so it was removed.
const SidebarItem = ({
  item,
  label,
  collapsed,
  badgeCount,
  badgeNoun = 'pending',
}: SidebarItemProps): React.JSX.Element => {
  const Icon = item.icon;
  const { bump } = useNavMru();
  const badgeLabel =
    badgeCount !== null && badgeCount !== undefined && badgeCount > 0
      ? badgeCount > 99
        ? '99+'
        : String(badgeCount)
      : null;

  const link = (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={() => bump(item.id)}
      className={({ isActive }) =>
        cn(
          'relative flex min-h-[30px] items-center gap-2.5 rounded-[var(--r-md)] px-2 py-1.5 text-[14px] transition-colors',
          isActive
            ? 'bg-[var(--z-100)] font-medium text-[var(--fg)]'
            : 'text-[var(--fg-muted)] hover:bg-[var(--z-100)] hover:text-[var(--fg)]',
          collapsed && 'justify-center',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'relative inline-flex w-4 flex-shrink-0 justify-center',
              isActive ? 'text-[var(--fg)]' : 'text-[var(--fg-faint)]',
            )}
          >
            <Icon className="h-[15px] w-[15px]" />
            {collapsed && badgeLabel ? (
              <span
                className="absolute -top-1.5 -right-1.5 inline-flex min-w-[14px] items-center justify-center rounded-full bg-red-500 px-[3px] text-[9px] font-semibold text-white"
                aria-hidden
              >
                {badgeLabel}
              </span>
            ) : null}
          </span>
          {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
          {!collapsed && badgeLabel ? (
            <span
              className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-semibold text-white tabular-nums"
              aria-label={`${badgeCount} ${badgeNoun}`}
            >
              {badgeLabel}
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );

  return collapsed ? (
    <UzTooltip label={label} side="right">
      {link}
    </UzTooltip>
  ) : (
    link
  );
};

/* ─── Collapsed section flyout ─── */

interface CollapsedGroupFlyoutProps {
  group: NavGroupConfig;
  /** Translated section name (also the flyout heading). */
  label: string;
  items: readonly NavItemConfig[];
  labelFor: (id: NavItemConfig['id']) => string;
  badgeFor: (id: string) => { count?: number; noun?: string };
}

/**
 * One parent icon per section in the collapsed rail. Clicking it opens a
 * flyout listing the section's items at full width (icon + name), so the
 * collapsed sidebar stays legible instead of a wall of ungrouped icons.
 */
const CollapsedGroupFlyout = ({
  group,
  label,
  items,
  labelFor,
  badgeFor,
}: CollapsedGroupFlyoutProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const GroupIcon = group.icon;
  const active = items.some(
    (it) => pathname === it.to || (it.to !== '/' && pathname.startsWith(`${it.to}/`)),
  );
  const totalBadge = items.reduce((n, it) => n + (badgeFor(it.id).count ?? 0), 0);

  // Close the flyout once navigation lands on the chosen page.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <UzPopover
      open={open}
      onOpenChange={setOpen}
      side="right"
      align="start"
      className="min-w-[210px] p-1"
      tooltip={label}
      tooltipSide="right"
      trigger={
        <button
          type="button"
          aria-label={label}
          className={cn(
            'flex min-h-[30px] items-center justify-center rounded-[var(--r-md)] px-2 py-1.5 text-[14px] transition-colors',
            active
              ? 'bg-[var(--z-100)] font-medium text-[var(--fg)]'
              : 'text-[var(--fg-muted)] hover:bg-[var(--z-100)] hover:text-[var(--fg)]',
          )}
        >
          <span
            className={cn(
              'relative inline-flex w-4 flex-shrink-0 justify-center',
              active ? 'text-[var(--fg)]' : 'text-[var(--fg-faint)]',
            )}
          >
            <GroupIcon className="h-[15px] w-[15px]" />
            {totalBadge > 0 ? (
              <span
                className="absolute -top-1.5 -right-1.5 inline-flex min-w-[14px] items-center justify-center rounded-full bg-red-500 px-[3px] text-[9px] font-semibold text-white"
                aria-hidden
              >
                {totalBadge > 99 ? '99+' : totalBadge}
              </span>
            ) : null}
          </span>
        </button>
      }
    >
      <p className="px-2 pt-1 pb-1.5 text-[10.5px] font-medium tracking-[0.07em] text-[var(--fg-faint)] uppercase">
        {label}
      </p>
      <div className="flex flex-col gap-px">
        {items.map((item) => {
          const badge = badgeFor(item.id);
          return (
            <SidebarItem
              key={item.id}
              item={item}
              label={labelFor(item.id)}
              collapsed={false}
              badgeCount={badge.count}
              badgeNoun={badge.noun}
            />
          );
        })}
      </div>
    </UzPopover>
  );
};

/* ─── Topbar ─── */

interface TopbarProps {
  onOpenMobileNav: () => void;
  user: ReturnType<typeof useCurrentUser>;
  initials: string;
  onSignOut: () => void;
}

const Topbar = ({ onOpenMobileNav, user, initials, onSignOut }: TopbarProps): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <header className="flex h-[49px] flex-shrink-0 items-center gap-4 border-b border-[var(--ds-border)] bg-[var(--bg)] px-4">
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="rounded-[var(--r-md)] p-1.5 text-[var(--fg-muted)] hover:bg-[var(--z-100)] lg:hidden"
        aria-label={t('shell.topbar.openMenu')}
      >
        <MenuIcon className="h-5 w-5" />
      </button>

      <div className="flex min-w-0 flex-1 items-center">
        <Breadcrumbs />
      </div>

      {/* The search box was dropped from the top bar (2026-10-29); ⌘K still opens the palette. */}

      <div className="flex flex-shrink-0 items-center gap-1">
        {/* <UzTooltip label={t('shell.topbar.help')}>
          <button
            type="button"
            className="hidden h-8 w-8 items-center justify-center rounded-[var(--r-md)] text-[var(--fg-muted)] hover:bg-[var(--z-100)] hover:text-[var(--fg)] md:inline-flex"
            aria-label={t('shell.topbar.help')}
          >
            <LifeBuoy className="h-4 w-4" />
          </button>
        </UzTooltip> */}
        {/* Installed app only, after "Later" on the new-version toast. */}
        <AppUpdateChip />
        {/* In-app inbox bell (ADR-0048). Polls /me/inbox every 30s; the
            unread badge is live across the session. */}
        <InboxBell />
        {/* 2026-06-25 - sliding top-right popup host. Renders next to
            the bell so it shares the auth-gated mount lifecycle.
            Subscribes to the same SSE stream; on each event it shows
            a card with snooze (10/20/30 min) + sound + Open lead. */}
        <NotificationPopupHost />
        <span
          className="mx-1 hidden h-[18px] w-px bg-[var(--ds-border)] md:inline-block"
          aria-hidden
        />
        {/* <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="hidden h-8 items-center gap-1.5 rounded-[var(--r-md)] px-2.5 text-[14px] text-[var(--fg-muted)] hover:bg-[var(--z-100)] hover:text-[var(--fg)] md:inline-flex"
        >
          <ExternalLink className="h-[13px] w-[13px]" />
          {t('shell.topbar.viewSite')}
        </a> */}

        <UzDropdownMenu
          items={[
            { type: 'label', label: user?.fullName ?? '' },
            {
              type: 'item',
              label: t('shell.user.profile'),
              icon: <UserIcon className="h-4 w-4" />,
              onSelect: () => navigate('/profile'),
            },
            {
              type: 'item',
              label: t('shell.topbar.help'),
              icon: <HelpCircle className="h-4 w-4" />,
            },
            { type: 'separator' },
            {
              type: 'item',
              label: t('auth.logout.label'),
              icon: <LogOut className="h-4 w-4" />,
              danger: true,
              onSelect: onSignOut,
            },
          ]}
          trigger={
            <button
              type="button"
              className="flex items-center gap-2 rounded-full py-1 pr-2 pl-1 hover:bg-[var(--z-100)]"
              aria-label={t('shell.topbar.userMenu')}
            >
              <UzAvatar initials={initials} name={user?.fullName ?? ''} size="sm" />
              <span className="hidden text-[14px] font-medium text-[var(--fg)] md:inline">
                {user?.fullName}
              </span>
              <ChevronDown className="h-4 w-4 text-[var(--fg-faint)]" />
            </button>
          }
        />
      </div>
    </header>
  );
};

/* ─── Breadcrumb ─── */

const Breadcrumbs = (): React.JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const user = useCurrentUser();
  const homeTo = homePathForRole(user?.role);
  const root = location.pathname.split('/').filter(Boolean)[0];
  const current = root ? NAV_ITEMS.find((i) => i.to === `/${root}`) : null;

  return (
    <nav className="flex min-w-0 items-center gap-1.5 text-[14px]" aria-label="Breadcrumb">
      <NavLink
        to={homeTo}
        className="truncate font-medium text-[var(--fg-muted)] transition-colors hover:text-[var(--fg)]"
      >
        {t('shell.topbar.home')}
      </NavLink>
      {current && (
        <>
          <ChevronRight className="h-3 w-3 flex-shrink-0 text-[var(--z-300)]" />
          <span className="truncate font-medium text-[var(--fg)]">
            {t(`shell.nav.${current.id}`)}
          </span>
        </>
      )}
    </nav>
  );
};
